import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Check } from "lucide-react";
import { monogramStyle, monogramText } from "@/lib/refclick-utils";
import { AVATARS } from "@/lib/avatars";
import { MOTIONS, OVERLAYS, chooseAnimation } from "@/lib/avatarAnimations";
import {
  EMOJI_POOL,
  loadCatPool,
  pickPoolAvatar,
} from "@/lib/avatarPool";

/**
 * Anker-Prime-45W-style animated "charging" icon.
 *
 * Wraps the existing monogram square with a circular progress ring
 * that mirrors today's click progress (count / effective_target):
 *
 *   idle (count = 0)      → dim ring outline, no motion (standby)
 *   charging (0 < count < target)  → filled arc + slow breathing glow
 *   full (count >= target) → ring closed + steady "fully charged"
 *                            state with a tiny check ✓ badge, and a
 *                            one-shot snap pulse the moment it fills
 *   on every +1           → lightning ⚡ flash + a soft scale bump
 *                            (piggybacks on the card's own count-bump)
 *
 * All motion is transform / opacity only (60fps friendly). Respects
 * ``prefers-reduced-motion`` — the breathing loop is skipped and the
 * ring transitions are shortened to a plain fade so we're not shaking
 * the icon for users who asked us not to.
 *
 * Purely cosmetic — never touches counts or earnings; if the ring's
 * numbers were ever wrong the +1 button still works because it has
 * its own state and click path.
 */
// Small helper mapping the overlay's `corner` code to Tailwind
// positioning. Kept as a plain function so future corners are one
// line to add.
function cornerClass(corner) {
  switch (corner) {
    case "tl":
      return "top-0.5 left-0.5 h-3.5 w-3.5";
    case "tr":
      return "top-0.5 right-0.5 h-3.5 w-3.5";
    case "bl":
      return "bottom-0.5 left-0.5 h-3.5 w-3.5";
    case "br":
      return "bottom-0.5 right-0.5 h-3.5 w-3.5";
    case "tc":
      return "top-0 left-1/2 h-3.5 w-3.5 -translate-x-1/2";
    default:
      return "top-0.5 right-0.5 h-3.5 w-3.5";
  }
}

export default function ChargerIcon({
  name,
  count = 0,
  target = 1,
  size = 44,
  variant = "row",
  avatarIndex = null,
  mood = "neutral",
  reaction = null,
}) {
  const safeCount = Math.max(0, Number(count) || 0);
  const safeTarget = Math.max(1, Number(target) || 1);
  const pct = Math.min(1, safeCount / safeTarget);
  const met = safeCount >= safeTarget;

  // Resolve the avatar (Feb-2026).
  //
  // Mood/reaction picks the BASE CHARACTER (the actual robot face), not
  // just a motion modifier. This is the fix the user asked for: on +1
  // the FACE visibly changes (idle-blink → pat-happy), not just the
  // same face wiggling. Only in `neutral` mood do we let the once-per-
  // hour shuffle (`avatarIndex`) pick a random character — this is the
  // "idle variety" the shuffle was originally there for.
  //
  //   plus     reaction → pat-happy   (excited)
  //   target   reaction → full-happy  (celebration — stays for the day)
  //   minus    reaction → dizzy       (brief sad flicker)
  //   sleep    mood     → sleep       (0 clicks)
  //   winner   mood     → full-happy
  //   full     mood     → full-happy
  //   overflow mood     → overheat
  //   loser    mood     → dizzy
  //   charging mood     → charging
  //   neutral  mood     → shuffled character (idle variety)
  // AVATARS order:
  //   0 idle-blink · 1 sleep · 2 charging · 3 full-happy
  //   4 pat-happy  · 5 dizzy · 6 swing    · 7 overheat
  //
  // Six-tier reaction ladder — one visibly different base per click
  // of the day. Screaming tier (plus4) intentionally uses `overheat`
  // for a wide-open-mouth "চিল্লানো" read; the tiers after that
  // stack overheat + swing + party motion for maximum hype.
  const REACTION_BASE = {
    plus1: 0,      // idle-blink — pleasant, mild smile via overlay
    plus2: 4,      // pat-happy  — clapping / happy
    plus3: 6,      // swing      — energised, mid-swing pose
    plus4: 7,      // overheat   — SCREAMING / mouth open (chillano)
    plus5: 7,      // overheat   — mind-blown territory
    plus6: 7,      // overheat   — maximum hype
    plus:  4,      // legacy fallback for the plain +1 event
    target: 3,     // full-happy
    minus: 5,      // dizzy
  };
  const MOOD_BASE     = { sleep: 1, winner: 3, full: 3, overflow: 7, starstruck: 3, loser: 5, charging: 2 };
  const resolvedIndex =
    (reaction && REACTION_BASE[reaction] !== undefined) ? REACTION_BASE[reaction]
      : (MOOD_BASE[mood] !== undefined)                 ? MOOD_BASE[mood]
      : (typeof avatarIndex === "number")               ? avatarIndex
      : 0;
  const avatar = AVATARS[resolvedIndex] || null;

  // Wave-2 avatars (Feb-2026): once we have a mood or reaction, roll
  // for a Noto Animated Emoji or a cat photo whose tags match. Emojis
  // and cats add ~80 tagged extras on top of the 8 SVGs. The pool item
  // WINS over the base SVG for that render — face swap is the whole
  // point of the mood engine, and Wave-2 makes the swap 10× more
  // visibly different than the 8 robot bases alone could.
  //
  // Seed: link name hash + hour bucket. Stable inside an hour, rolls
  // to a fresh face after each hourly shuffle. When the user's
  // reduced-motion setting is on we always fall back to the SVG
  // (emojis are animated WebPs — reduced-motion still gets the frame
  // but at least we skip the extra motion layers we might stack).
  const [catsPool, setCatsPool] = useState(() => (typeof window !== "undefined" && window.__refclickCatPool) || []);
  useEffect(() => {
    if (catsPool.length > 0) return; // already loaded
    loadCatPool().then((list) => setCatsPool(list));
  }, [catsPool.length]);

  // Deterministic seed from the display name so two different links
  // don't share the same emoji in the same hour. `avatarIndex` (the
  // hourly shuffle tick) is folded in so the choice rotates hourly.
  const poolSeed = useMemo(() => {
    let h = 2166136261 >>> 0;
    const src = String(name || "?") + "::" + String(avatarIndex || 0);
    for (let i = 0; i < src.length; i += 1) {
      h ^= src.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }, [name, avatarIndex]);

  // Pool-avatar picking:
  //   - Reactions ALWAYS try the pool first (that's where the +4
  //     "chillano" screaming emojis live).
  //   - Moods try the pool 65% of the time to keep some robot variety.
  //   - Otherwise we render the base SVG below.
  const poolPick = useMemo(() => {
    const key = reaction || mood || "neutral";
    // Deterministic 0-99 gate on the same seed. `>= 35` gives a
    // 65% chance of choosing a pool item, which mixes in the SVG
    // robot for the remaining 35% so the character shuffle still
    // occasionally lands on the classic robots.
    const gate = poolSeed % 100;
    if (!reaction && gate < 35) return null;
    return pickPoolAvatar(key, poolSeed, catsPool);
  }, [reaction, mood, poolSeed, catsPool]);

  // Mood-driven animation pick. `chooseAnimation` deterministically
  // returns a registry entry for the (mood, base, reaction) tuple.
  const animation = chooseAnimation(mood, resolvedIndex, reaction);
  const overlay = animation ? OVERLAYS[animation.overlay] : null;

  // prefers-reduced-motion — cache once per mount. useState so the
  // ternaries below stay pure JSX; the value never changes at runtime.
  const [reduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  });

  // Detect the transitions we want to react to ONCE per change:
  //   * count went up → fire ⚡ bolt-flash
  //   * pct crossed 1 for the first time → fire "just filled" snap
  const prevCountRef = useRef(safeCount);
  const prevMetRef = useRef(met);
  const [boltKey, setBoltKey] = useState(0);
  const [snapKey, setSnapKey] = useState(0);
  useEffect(() => {
    if (safeCount > prevCountRef.current) setBoltKey((k) => k + 1);
    prevCountRef.current = safeCount;
  }, [safeCount]);
  useEffect(() => {
    if (met && !prevMetRef.current) setSnapKey((k) => k + 1);
    prevMetRef.current = met;
  }, [met]);

  // SVG ring geometry — 2px stroke around the monogram with a 2px gap
  // to the letter so the ring reads as "around" the icon, not on it.
  const stroke = 2.2;
  const r = size / 2 - stroke - 1;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - pct);

  // Ring visibility per state:
  //   idle → 12% alpha outline only
  //   charging → 30% alpha track + accent arc
  //   full → accent full ring + light glow
  const trackAlpha = safeCount === 0 ? 0.12 : 0.28;

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      data-testid="charger-icon"
      data-state={met ? "full" : safeCount === 0 ? "idle" : "charging"}
      data-pct={pct.toFixed(3)}
      aria-hidden
    >
      {/* Breathing halo — only while charging, and only when not
          reduced-motion. Sits BEHIND the monogram so it feels like a
          glow bleeding out of the charger, not an outline on top. */}
      {!met && safeCount > 0 && !reduced && (
        <motion.span
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgb(var(--accent) / 0.35) 0%, transparent 65%)",
          }}
          animate={{ opacity: [0.35, 0.75, 0.35], scale: [0.9, 1.06, 0.9] }}
          transition={{
            duration: 2.4,
            ease: "easeInOut",
            repeat: Infinity,
          }}
        />
      )}

      {/* Full-brightness one-shot pulse the moment we hit target. Keyed
          so it re-fires on every 0→1 transition (undo + reclimb). */}
      <AnimatePresence>
        {met && (
          <motion.span
            key={`full-halo-${snapKey}`}
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgb(var(--accent) / 0.55) 0%, transparent 70%)",
            }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: [0, 0.85, 0.35], scale: [0.85, 1.15, 1] }}
            transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
          />
        )}
      </AnimatePresence>

      {/* SVG ring — behind the monogram. -rotate-90 puts 0° at the top
          so the arc sweeps clockwise like a real charger's fuel gauge. */}
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="pointer-events-none absolute inset-0 -rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeOpacity={trackAlpha}
          strokeWidth={stroke}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          className="text-accent"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={false}
          animate={{ strokeDashoffset: dashOffset }}
          transition={
            reduced
              ? { duration: 0.15, ease: "linear" }
              : {
                  type: "spring",
                  stiffness: 240,
                  damping: 20,
                  mass: 0.4,
                }
          }
          style={{
            filter: met ? "drop-shadow(0 0 4px rgb(var(--accent) / 0.9))" : undefined,
          }}
        />
      </svg>

      {/* Avatar layer — an animated SVG robot filling the inner
          disc, OR a Wave-2 pool avatar (Noto emoji WebP / cat photo)
          when the mood engine picked one. Cross-faded via
          AnimatePresence so the once-a-hour reshuffle transitions
          smoothly instead of hard-swapping. Falls back to the
          classic colored monogram square when nothing is available
          yet (SSR-safe first paint). */}
      <div
        className="absolute inset-[3px] overflow-hidden rounded-full"
        style={
          poolPick || avatar
            ? { background: "rgb(10 12 16 / 0.55)" }
            : monogramStyle(name)
        }
        data-testid="charger-monogram"
        data-mood={mood}
        data-reaction={reaction || "none"}
        data-pool={poolPick ? poolPick.kind : "svg"}
        data-pool-key={poolPick ? poolPick.key : ""}
        title={poolPick ? `${poolPick.bn} · ${poolPick.alt}` : undefined}
      >
        {poolPick ? (
          // Wave-2 render: an <img> for the emoji WebP or cat photo.
          // Motion wrapper still applies mood/reaction motion so a
          // screaming emoji still shakes; the base swap is what
          // visibly reads as "different face on every tier".
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={`pool::${poolPick.key}::${animation.name}`}
              className="absolute inset-0"
              initial={{ opacity: 0, scale: 0.85 }}
              animate={
                reduced
                  ? { opacity: 1, scale: 1 }
                  : {
                      opacity: 1,
                      ...MOTIONS[animation.motion]?.animate,
                    }
              }
              exit={{ opacity: 0, scale: 1.08 }}
              transition={
                reduced
                  ? { duration: 0.4 }
                  : {
                      opacity: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
                      ...(MOTIONS[animation.motion]?.transition || {}),
                    }
              }
              style={{ willChange: "transform, opacity" }}
              data-testid="avatar-motion"
              data-animation={animation.name}
            >
              <motion.img
                src={poolPick.url}
                alt={poolPick.alt}
                draggable={false}
                loading="lazy"
                decoding="async"
                animate={{ scale: boltKey ? [1, 1.08, 1] : 1 }}
                transition={{
                  type: "spring",
                  stiffness: 560,
                  damping: 18,
                  mass: 0.4,
                }}
                className={
                  "absolute inset-0 h-full w-full " +
                  (poolPick.kind === "cat" ? "object-cover" : "object-contain")
                }
                style={{ willChange: "transform" }}
              />
            </motion.div>
          </AnimatePresence>
        ) : avatar ? (
          <AnimatePresence mode="popLayout" initial={false}>
            {/* The MOTION MODIFIER wrapper: applies the mood/reaction
                animation (bounce / spin / one-jump / etc.). Keyed on
                both the base swap AND the animation name so a mood
                change restarts the loop cleanly instead of bleeding
                into the previous one. When reduced-motion is on we
                bail out to a static pose. */}
            <motion.div
              key={`${avatar.key}::${animation.name}`}
              className="absolute inset-0"
              style={{ willChange: "transform, opacity" }}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={
                reduced
                  ? { opacity: 1, scale: 1 }
                  : {
                      opacity: 1,
                      ...MOTIONS[animation.motion]?.animate,
                    }
              }
              exit={{ opacity: 0, scale: 1.08 }}
              transition={
                reduced
                  ? { duration: 0.4 }
                  : {
                      opacity: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
                      ...(MOTIONS[animation.motion]?.transition || {}),
                    }
              }
              data-testid="avatar-motion"
              data-animation={animation.name}
            >
              <motion.img
                src={avatar.src}
                alt={avatar.alt}
                draggable={false}
                loading="lazy"
                decoding="async"
                animate={{ scale: boltKey ? [1, 1.08, 1] : 1 }}
                transition={{
                  type: "spring",
                  stiffness: 560,
                  damping: 18,
                  mass: 0.4,
                }}
                className="absolute inset-0 h-full w-full object-contain"
                style={{ willChange: "transform" }}
              />
            </motion.div>
          </AnimatePresence>
        ) : (
          <motion.span
            className={
              "flex h-full w-full items-center justify-center font-display font-bold text-white/95 " +
              (variant === "card" ? "text-sm" : "text-[13px]")
            }
            animate={{ scale: boltKey ? [1, 1.07, 1] : 1 }}
            transition={{ type: "spring", stiffness: 560, damping: 18, mass: 0.4 }}
          >
            {monogramText(name)}
          </motion.span>
        )}

        {/* Overlay badge — hearts / stars / zzz / fire / etc. floats in
            a corner of the avatar frame. Purely cosmetic layer,
            AnimatePresence-keyed so mood changes swap it out with a
            gentle fade instead of a hard cut. */}
        <AnimatePresence>
          {overlay && !reduced && (
            <motion.span
              key={`${animation.name}-overlay`}
              aria-hidden
              initial={{ opacity: 0, scale: 0.6, y: 2 }}
              animate={{ opacity: [0, 1, 0.85], scale: [0.6, 1.08, 1], y: 0 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className={
                "pointer-events-none absolute z-10 flex items-center justify-center rounded-full text-[9px] font-black leading-none " +
                cornerClass(overlay.corner)
              }
              style={{
                color: overlay.color,
                textShadow: `0 0 6px ${overlay.color}, 0 0 12px ${overlay.color}80`,
              }}
              data-testid="avatar-overlay"
              data-overlay={animation.overlay}
            >
              {overlay.char}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Lightning-bolt flash on every +1. Keyed so a rapid burst of
          clicks re-fires cleanly. */}
      <AnimatePresence>
        {boltKey > 0 && (
          <motion.span
            key={`bolt-${boltKey}`}
            className="pointer-events-none absolute inset-0 flex items-center justify-center text-accent"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: [0, 0.95, 0], scale: [0.6, 1.15, 1.35] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
          >
            <Zap
              className="h-5 w-5 drop-shadow-[0_0_6px_rgb(var(--accent)_/_0.9)]"
              strokeWidth={2.5}
              fill="currentColor"
            />
          </motion.span>
        )}
      </AnimatePresence>

      {/* "Fully charged" corner badge — tiny check ✓ once target's met.
          Stays until count drops below target. */}
      <AnimatePresence>
        {met && (
          <motion.span
            key="full-badge"
            className="absolute -bottom-0.5 -right-0.5 flex h-[14px] w-[14px] items-center justify-center rounded-full bg-accent text-[8px] text-black/85 shadow-[0_0_6px_rgb(var(--accent)_/_0.9)]"
            initial={{ opacity: 0, scale: 0.4, rotate: -25 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.4 }}
            transition={{ type: "spring", stiffness: 520, damping: 18 }}
          >
            <Check className="h-2.5 w-2.5" strokeWidth={3} />
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}
